// Acheter dans la boutique « Investir en toi », cumuler les effets des achats, et user le matériel.
import { UPGRADES, type UpgradeDef, type UpgradeEffect } from '../content/upgrades';
import { round2 } from '../engine/finance';
import { t } from '../engine/i18n';
import type { GameState } from '../engine/state';
import { DAYS_PER_YEAR } from '../engine/time';
import { addJournal, addNotice, addWellbeing, unlockPage } from './actions';
import { atTodayPrices } from './economy';
import { purchasePenalty } from './decouvert';

export function getUpgrade(id: string): UpgradeDef | undefined {
  return UPGRADES.find((upgrade) => upgrade.id === id);
}

/** Somme d'un effet sur tous les achats qui font effet : le matériel usé ne compte plus. */
export function upgradeBonus(state: GameState, key: keyof UpgradeEffect): number {
  return state.upgrades.reduce((sum, id) => sum + (getUpgrade(id)?.effect[key] ?? 0), 0);
}

export function visibleUpgrades(state: GameState): UpgradeDef[] {
  return UPGRADES.filter(
    (upgrade) =>
      state.upgrades.includes(upgrade.id) || state.wornUpgrades.includes(upgrade.id) || upgrade.visible(state),
  );
}

/** Âge d'un achat en jours, depuis son achat ou son dernier remplacement. */
export function upgradeAge(state: GameState, id: string): number {
  return state.day - (state.upgradeDays[id] ?? state.day);
}

/** Jours avant que le matériel soit usé ; null pour ce qui ne s'use pas. */
export function daysBeforeWorn(state: GameState, upgrade: UpgradeDef): number | null {
  if (!upgrade.lifespan) return null;
  return Math.max(0, upgrade.lifespan.years * DAYS_PER_YEAR - upgradeAge(state, upgrade.id));
}

/** Le prix d'un achat de la boutique, au niveau des prix du moment. */
export function upgradePrice(state: GameState, upgrade: UpgradeDef): number {
  return Math.round(atTodayPrices(state, upgrade.price) * purchasePenalty(state));
}

export function buyUpgrade(state: GameState, id: string): void {
  const upgrade = getUpgrade(id);
  if (!upgrade || state.upgrades.includes(id)) return;
  const price = upgradePrice(state, upgrade);
  if (state.cash < price) return;
  const replacing = state.wornUpgrades.includes(id);
  if (!replacing && !upgrade.visible(state)) return;
  state.cash = round2(state.cash - price);
  state.upgrades.push(id);
  state.upgradeDays[id] = state.day;
  if (replacing) {
    // Remplacer rend le bonus, sans rejouer les effets du premier achat.
    state.wornUpgrades = state.wornUpgrades.filter((worn) => worn !== id);
    addJournal(state, 'journal.upgradeReplaced', { title: t(upgrade.title) });
    return;
  }
  const { effect } = upgrade;
  if (effect.wellbeing) addWellbeing(state, effect.wellbeing);
  if (effect.energyMax) state.energy += effect.energyMax;
  // Avant le premier emploi, le bonus de salaire s'appliquera au salaire de départ.
  if (effect.salary && state.firstJobDay !== null) state.salary = round2(state.salary * (1 + effect.salary));
  addJournal(state, 'journal.upgrade', { title: t(upgrade.title) });
}

/** Chaque jour : le matériel arrivé au bout de sa durée de vie s'use, et son bonus s'arrête. */
export function wearUpgrades(state: GameState): void {
  for (const id of [...state.upgrades]) {
    const upgrade = getUpgrade(id);
    if (!upgrade?.lifespan || daysBeforeWorn(state, upgrade) !== 0) continue;
    state.upgrades = state.upgrades.filter((owned) => owned !== id);
    state.wornUpgrades.push(id);
    addNotice(state, upgrade.lifespan.worn);
    addJournal(state, upgrade.lifespan.worn);
    unlockPage(state, 'usure');
  }
}

/**
 * Un appareil remplacé hors de la boutique (une panne, un achat dans un événement) repart pour
 * toute sa durée de vie ; une réparation (une batterie neuve) lui rend seulement quelques années.
 */
export function renewUpgrade(state: GameState, id: string, yearsLeft?: number): void {
  if (!state.upgrades.includes(id) && !state.wornUpgrades.includes(id)) return;
  const upgrade = getUpgrade(id);
  const lifespanDays = (upgrade?.lifespan?.years ?? 0) * DAYS_PER_YEAR;
  const renewedDay =
    yearsLeft === undefined ? state.day : state.day - Math.max(0, lifespanDays - yearsLeft * DAYS_PER_YEAR);
  if (state.wornUpgrades.includes(id)) {
    state.wornUpgrades = state.wornUpgrades.filter((worn) => worn !== id);
    state.upgrades.push(id);
    state.upgradeDays[id] = renewedDay;
    return;
  }
  // Une réparation ne raccourcit jamais la vie qui restait.
  state.upgradeDays[id] = Math.max(state.upgradeDays[id] ?? state.day, renewedDay);
}
